/**
 * DOM Translation Patch (self-executing)
 *
 * Mitigates a long-standing React defect (facebook/react#11538, #17256) where
 * browser translation engines (Google Translate, Edge Translate, etc.) mutate
 * the DOM outside of React's control. Translation replaces text nodes with
 * <font> wrappers, breaking the parent-child relationships React expects.
 * When React later calls removeChild/insertBefore during reconciliation, the
 * browser throws "The node to be removed is not a child of this node."
 *
 * Importing this module is sufficient — the patch applies automatically on
 * first import in a browser environment.
 */

import { logger } from "@/lib/utils/logger";

let patchHitCount = 0;
const TELEMETRY_THRESHOLD = 10;

function reportPatchHit(method: string): void {
  patchHitCount++;

  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `[DOM Translation Patch] ${method}: node was reparented by a translation engine — suppressing error`,
    );
    return;
  }

  if (patchHitCount <= TELEMETRY_THRESHOLD) {
    logger.warn(
      `[DOM Translation Patch] ${method}: suppressed reparented node error`,
      {
        hitCount: patchHitCount,
        url: globalThis.location?.href,
      },
    );
  }
}

function applyDomTranslationPatch(): void {
  if (globalThis.window === undefined) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      reportPatchHit("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportPatchHit("insertBefore");
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

applyDomTranslationPatch();
